import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, type QueryKey } from '@tanstack/react-query';
import { Replace } from 'lucide-react';
import { vendorFromSerial, type KnownOnuRecord, type OnuSwapPlan } from '@simpleacs/shared';
import { api } from '../../lib/api';
import { swapReplacementModel } from '../../lib/swap-model';
import { useTasks } from '../../lib/tasks';
import { WizardDialog } from '../../components/WizardDialog';
import {
  Button,
  EmptyState,
  ErrorNotice,
  Field,
  Pill,
  SearchSelect,
  Spinner,
} from '../../components/ui';

/** One row of GET /olts/:id/unconfigured. */
interface WaitingOnu {
  ponPort: string;
  serial: string;
  state: string;
  detectedModel?: string;
  knownElsewhere: KnownOnuRecord | null;
}

interface HardwareModel {
  id: string;
  name: string;
  vendor: string | null;
  /** How many ONUs on the network already run it — the library's tie-break. */
  onuCount?: number;
}

/**
 * Swap: the client's ONU died and the engineer has hung a new one on the drop.
 *
 * Three steps, in the order the decision is made. PICK the replacement from
 * what the chassis sees waiting unauthorised — the same PON port first and
 * marked, because that is the drop the engineer is standing at — and say what
 * model it is (a Huawei autofind reports it; a ZTE `uncfg` does not, so the
 * outgoing unit's model is prefilled for a like-for-like RMA — only while the
 * two serials name the same vendor, `swapReplacementModel`). REVIEW
 * the dry run: what the chassis will be sent, what is carried across, what the
 * new model cannot take. APPLY (the operator picked the unit and read the plan; no re-typing) — the serial is
 * the one fact this dialog exists to get right — which closes the dialog and
 * runs the chassis session in the tray, like Recreate and Delete.
 *
 * Nothing is deleted on this side: the subscriber keeps their number, name and
 * everything else; only the serial and model on their record change, and the
 * Swaps tab keeps the old unit.
 */
export function SwapOnuDialog({
  olt,
  onu,
  onClose,
  href,
  invalidate,
}: {
  olt: { id: string; name: string; vendor: string | null };
  onu: { id: string; serial: string; ponPort: string; onuIndex: string; onuType: string | null };
  onClose: () => void;
  /** Where the tray row links once the chassis has answered — the ONU's page. */
  href?: string;
  /** Extra query keys to refresh once the swap lands. */
  invalidate?: QueryKey[];
}) {
  const tasks = useTasks();
  const [step, setStep] = useState(0);
  const [newSerial, setNewSerial] = useState('');
  const [onuType, setOnuType] = useState(onu.onuType ?? '');
  const [plan, setPlan] = useState<OnuSwapPlan | null>(null);
  const [error, setError] = useState<string | null>(null);

  const waiting = useQuery({
    queryKey: ['olt', olt.id, 'unconfigured'],
    queryFn: () =>
      api.get<{ items: WaitingOnu[]; warnings: string[] }>(
        `/olts/${encodeURIComponent(olt.id)}/unconfigured`,
      ),
    staleTime: 0,
  });
  const hardware = useQuery({
    queryKey: ['onu-hardware'],
    queryFn: () => api.get<{ items: HardwareModel[] }>('/onu-hardware'),
  });

  /** Same drop first, then the rest of the chassis; each marked for which it is. */
  const candidates = useMemo(() => {
    const items = waiting.data?.items ?? [];
    return [...items].sort((a, b) => {
      const aSame = a.ponPort === onu.ponPort ? 0 : 1;
      const bSame = b.ponPort === onu.ponPort ? 0 : 1;
      return aSame - bSame || a.ponPort.localeCompare(b.ponPort) || a.serial.localeCompare(b.serial);
    });
  }, [waiting.data, onu.ponPort]);
  const chosen = candidates.find((c) => c.serial === newSerial) ?? null;

  // A Huawei autofind names the model; take it when the operator has not.
  useEffect(() => {
    if (chosen?.detectedModel && !onuType) setOnuType(chosen.detectedModel);
  }, [chosen, onuType]);

  /*
    Every model the library holds — a ZTE chassis carries Huawei ONTs (gachie1
    runs 142 HG8546Ms), so filtering by the OLT's vendor hid the very type a
    Huawei replacement needs. The replacement's own vendor sorts first.
  */
  const incomingVendor = newSerial ? vendorFromSerial(newSerial)?.toLowerCase() ?? null : null;
  const models = [...(hardware.data?.items ?? [])].sort(
    (a, b) =>
      Number((b.vendor ?? '').toLowerCase() === incomingVendor) - Number((a.vendor ?? '').toLowerCase() === incomingVendor) ||
      (b.onuCount ?? 0) - (a.onuCount ?? 0) ||
      a.name.localeCompare(b.name),
  );
  const modelOptions = models.map((m) => ({ value: m.name, label: m.vendor ? `${m.name} · ${m.vendor}` : m.name }));
  if (onuType && !modelOptions.some((o) => o.value === onuType)) {
    modelOptions.unshift({ value: onuType, label: onuType });
  }

  const steps = [
    { key: 'pick', title: 'Pick the replacement' },
    { key: 'review', title: 'Review' },
    { key: 'swap', title: 'Apply' },
  ];
  const current = steps[Math.min(step, steps.length - 1)]!;

  const path = `/olts/${encodeURIComponent(olt.id)}/onus/${encodeURIComponent(onu.id)}/swap`;
  const body = (dryRun: boolean) => ({
    newSerial,
    ...(onuType ? { onuType } : {}),
    dryRun,
  });
  const preview = useMutation({
    mutationFn: () => api.post<OnuSwapPlan>(path, body(true)),
    onSuccess: (r) => {
      setPlan(r);
      setError(null);
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Could not build the plan'),
  });

  /**
   * Swap CLOSES the dialog and hands the chassis session to the tray. The
   * dry run above stays in the dialog; the operator has read it by now.
   */
  const startSwap = () => {
    const payload = body(false);
    const label = `Swap ${onu.serial} → ${newSerial}`;
    onClose();
    tasks.start<OnuSwapPlan>({
      label,
      ...(href ? { href } : {}),
      run: () => api.post<OnuSwapPlan>(path, payload),
      done: (r) =>
        r.warnings.length === 0
          ? `${newSerial} is now ${onu.ponPort}:${onu.onuIndex} — the OLT accepted the swap.`
          : `${newSerial} is now ${onu.ponPort}:${onu.onuIndex}, with notes — open the ONU to read them.`,
      invalidate: [['onu', onu.id], ['onus'], ['olt', olt.id], ...(invalidate ?? [])],
    });
  };

  const canPick = Boolean(chosen) && !chosen?.knownElsewhere && onuType.trim().length >= 2;

  const next = () => {
    // The plan is the Review step's dry-run of the CHOSEN unit; it is thrown
    // away only when the choice can change (leaving Pick), never on the way to
    // Apply — clearing it there left the last step empty with Apply disabled.
    if (current.key === 'pick') {
      setPlan(null);
      preview.mutate();
    }
    setStep((s) => Math.min(s + 1, steps.length - 1));
  };

  return (
    <WizardDialog
      title={
        <span className="flex items-center gap-2">
          <Replace className="size-4 text-muted" />
          Swap {onu.serial}
        </span>
      }
      subtitle={`${olt.name} · ${onu.ponPort}:${onu.onuIndex} — a different unit takes this subscriber's slot; the subscriber drops while it rebuilds.`}
      maxWidth="2xl"
      steps={steps}
      currentStep={step}
      onClose={onClose}
      footer={
        <div className="flex items-center justify-between gap-2">
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <div className="flex gap-2">
            {step > 0 && (
              <Button
                onClick={() => {
                  setPlan(null);
                  setStep((s) => s - 1);
                }}
              >
                Back
              </Button>
            )}
            {current.key === 'pick' && (
              <Button variant="primary" disabled={!canPick} onClick={next}>
                Next
              </Button>
            )}
            {current.key === 'review' && (
              <Button variant="primary" disabled={!plan} onClick={next}>
                Next
              </Button>
            )}
            {current.key === 'swap' && (
              <Button variant="danger" disabled={!plan} onClick={startSwap}>
                Apply
              </Button>
            )}
          </div>
        </div>
      }
    >
      {current.key === 'pick' && (
        <div className="space-y-4">
          <Field
            label="Waiting on the OLT"
            hint={`Unauthorised ONUs ${olt.name} can see. Units on ${onu.ponPort} — this subscriber's drop — come first.`}
          >
            {waiting.isLoading ? (
              <div className="flex justify-center p-6">
                <Spinner />
              </div>
            ) : waiting.isError ? (
              <ErrorNotice error={waiting.error} />
            ) : candidates.length === 0 ? (
              <EmptyState
                title="Nothing is waiting"
                description={`${olt.name} lists no unauthorised ONU. Check the replacement has light, then look again — the list is read live each time this opens.`}
              />
            ) : (
              <ul className="divide-y divide-line rounded-lg border border-line">
                {candidates.map((c) => {
                  const sameDrop = c.ponPort === onu.ponPort;
                  const moved = c.knownElsewhere;
                  const selected = c.serial === newSerial;
                  return (
                    <li key={`${c.ponPort}:${c.serial}`}>
                      <button
                        type="button"
                        disabled={Boolean(moved)}
                        onClick={() => {
                          setNewSerial(c.serial);
                          setPlan(null);
                          // A change of unit re-derives the model: the autofind's
                          // where there is one, the outgoing unit's only while the
                          // serials name the same vendor, else the library's guess.
                          setOnuType(
                            swapReplacementModel({
                              newSerial: c.serial,
                              detectedModel: c.detectedModel ?? null,
                              outgoingSerial: onu.serial,
                              outgoingType: onu.onuType,
                              models: hardware.data?.items ?? [],
                            }),
                          );
                        }}
                        className={`flex w-full flex-wrap items-center gap-3 px-4 py-3 text-left ${
                          selected ? 'bg-primary-soft' : moved ? 'cursor-not-allowed opacity-60' : 'hover:bg-surface-2'
                        }`}
                      >
                        <span className="font-mono text-sm text-ink">{c.serial}</span>
                        <span className="text-xs text-muted">{c.detectedModel ?? 'model not reported'}</span>
                        <span className="font-mono text-xs text-faint">{c.ponPort}</span>
                        <span className="text-xs text-faint">{c.state}</span>
                        {sameDrop && <Pill tone="success">same drop</Pill>}
                        {!sameDrop && <Pill tone="warn">other PON port</Pill>}
                        {moved && (
                          <span className="basis-full text-xs text-warn">
                            Recorded on {moved.oltName} {moved.ponPort}:{moved.onuIndex}
                            {moved.name ? ` (${moved.name})` : ''} — that is a move, not a replacement. Use Move.
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
            {(waiting.data?.warnings ?? []).map((w) => (
              <p key={w} className="mt-2 text-xs text-warn">
                {w}
              </p>
            ))}
          </Field>

          <Field
            label="Replacement model"
            hint={
              olt.vendor === 'zte'
                ? `A ZTE chassis does not report a waiting unit's model, so it is taken from the replacement's serial${onu.onuType ? ` — ${onu.serial} is a ${onu.onuType}, kept only when the replacement is the same make` : ''}. Check it: the OLT declares the unit as this type.`
                : 'From the autofind where the chassis reports it. Change it if the engineer fitted something else.'
            }
          >
            <SearchSelect
              value={onuType}
              options={modelOptions}
              onChange={(v) => {
                setOnuType(v);
                setPlan(null);
              }}
              placeholder="ONU type as the OLT knows it"
            />
          </Field>
        </div>
      )}

      {current.key === 'review' && (
        <div className="space-y-4">
          {preview.isPending ? (
            <div className="flex justify-center p-6">
              <Spinner />
            </div>
          ) : error ? (
            <ErrorNotice error={new Error(error)} />
          ) : plan ? (
            <>
              <div className="grid gap-3 sm:grid-cols-2">
                <Unit heading="Leaves" serial={plan.swap.previous.serial} model={plan.swap.previous.model} />
                <Unit
                  heading="Takes its place"
                  serial={plan.swap.replacement.serial}
                  model={plan.swap.replacement.model}
                  note={
                    plan.swap.replacement.sameDrop
                      ? `seen on ${plan.swap.replacement.seenOnPonPort}, the same drop`
                      : `seen on ${plan.swap.replacement.seenOnPonPort} — not this subscriber's port`
                  }
                />
              </div>
              {plan.warnings.map((w) => (
                <div
                  key={w}
                  className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-sm text-warn"
                >
                  {w}
                </div>
              ))}
              {plan.swap.dropped.length > 0 && (
                <div className="rounded-lg border border-danger/40 bg-danger-soft px-3 py-2 text-sm text-danger">
                  Not carried onto {plan.swap.replacement.model}: {plan.swap.dropped.join('; ')}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}

      {current.key === 'swap' && plan && (
        <div className="space-y-4">
          <div className="rounded-lg border border-danger/40 bg-danger-soft px-4 py-3">
            <p className="text-sm font-medium text-danger">
              {onu.serial} is cleared from {olt.name} and {newSerial} is declared in its place.
            </p>
            <p className="mt-1 text-sm text-danger/90">
              The subscriber keeps their number, name, WAN, management, WiFi, VLANs and ports. The old
              router is deleted from the ACS; only its serial and model stay, on the Swaps tab. The
              subscriber drops while the slot rebuilds. This closes and runs on the chassis in the
              background — watch the tray for what the OLT says.
            </p>
          </div>
        </div>
      )}
    </WizardDialog>
  );
}

function Unit({
  heading,
  serial,
  model,
  note,
}: {
  heading: string;
  serial: string;
  model: string | null;
  note?: string;
}) {
  return (
    <div className="rounded-lg border border-line px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-faint">{heading}</div>
      <div className="mt-1 font-mono text-sm text-ink">{serial}</div>
      <div className="mt-0.5 text-xs text-muted">{model ?? 'Model not reported'}</div>
      {note && <div className="mt-0.5 text-xs text-faint">{note}</div>}
    </div>
  );
}
